/**
 * Landlord in-app notification type registry.
 *
 * The single, static, shared source of truth for every landlord in-app
 * notification type: its category, display label/description (for the
 * dynamic preferences UI), whether it is suppressible, its priority tier
 * (which determines its default fan-out channels — MF-BR-76), and the
 * `referenceType` its instances' `referenceId` points at (MF-BR-77).
 *
 * A `type` not present here is an error at the orchestrator
 * (`getNotificationTypeDefinition`) — a new call site must register itself
 * here first.
 */
/** Priority -> its default channel set. A class's explicit `channels` overrides this (MF-BR-76). */
export const TIER_DEFAULT_CHANNELS = {
    ambient: ['in_app'],
    standard: ['in_app'],
    urgent: ['in_app', 'email', 'push'],
};
/** Resolve a definition's effective fan-out channels — its override, or its tier's default. */
export function resolveChannels(def) {
    return def.channels ?? TIER_DEFAULT_CHANNELS[def.priority];
}
/**
 * BR-14 — `ambient` types never appear in the notification bell, by default.
 * BR-82 — `bellVisible`, when set, overrides the tier-derived default in either
 * direction (e.g. an `urgent` class hidden from the bell while still fanning out).
 */
export function isBellVisible(def) {
    return def.bellVisible ?? def.priority !== 'ambient';
}
// `satisfies` (rather than a `: Record<...>` type annotation) keeps each key's literal
// type intact, so `keyof typeof NOTIFICATION_TYPE_REGISTRY` below yields the union of the
// real type strings — not the widened `string` an explicit annotation would produce.
export const NOTIFICATION_TYPE_REGISTRY = {
    external_payments_promoted: {
        type: 'external_payments_promoted',
        category: 'payments_banking',
        label: 'External Payments Promoted',
        description: 'An externally detected payment was automatically promoted to a matched transaction.',
        suppressible: true,
        priority: 'urgent',
        referenceType: 'lease',
    },
    external_payment_unmatched: {
        type: 'external_payment_unmatched',
        category: 'payments_banking',
        label: 'Unmatched External Payment',
        description: 'An externally detected payment could not be automatically matched to a lease or transaction.',
        suppressible: false,
        priority: 'urgent',
        referenceType: 'linked_bank_account',
    },
    external_payment_auto_applied: {
        type: 'external_payment_auto_applied',
        category: 'payments_banking',
        label: 'External Payment Auto-Applied',
        description: "An externally detected payment was automatically applied to a tenant's ledger.",
        suppressible: true,
        priority: 'urgent',
        referenceType: 'bank_transaction',
    },
    external_payment_detected: {
        type: 'external_payment_detected',
        category: 'payments_banking',
        label: 'External Payment Detected',
        description: 'A new external payment was detected and requires review.',
        suppressible: false,
        priority: 'urgent',
        referenceType: 'bank_transaction',
    },
    late_fee_assessed: {
        type: 'late_fee_assessed',
        category: 'payments_banking',
        label: 'Late Fee Assessed',
        description: "A late fee was assessed against a tenant's account.",
        suppressible: false,
        priority: 'urgent',
        referenceType: 'lease',
    },
    payment_manual_review: {
        type: 'payment_manual_review',
        category: 'payments_banking',
        label: 'Payment Requires Manual Review',
        description: 'A payment requires manual review before it can be reconciled.',
        suppressible: false,
        priority: 'urgent',
        referenceType: 'payment',
    },
    bank_reconnection_required: {
        type: 'bank_reconnection_required',
        category: 'payments_banking',
        label: 'Bank Reconnection Required',
        description: 'A connected bank account needs to be reconnected to continue syncing.',
        suppressible: false,
        priority: 'urgent',
        referenceType: 'linked_bank_account',
    },
    bank_connection_error: {
        type: 'bank_connection_error',
        category: 'payments_banking',
        label: 'Bank Connection Error',
        description: 'An error occurred with a connected bank account.',
        suppressible: false,
        priority: 'urgent',
        referenceType: 'linked_bank_account',
    },
    bank_connection_expiring: {
        type: 'bank_connection_expiring',
        category: 'payments_banking',
        label: 'Bank Connection Expiring',
        description: "A connected bank account's authorization is expiring soon.",
        suppressible: false,
        priority: 'urgent',
        referenceType: 'linked_bank_account',
    },
    lease_conflict_detected: {
        type: 'lease_conflict_detected',
        category: 'leasing',
        label: 'Lease Conflict Detected',
        description: 'A conflict was detected between overlapping leases or lease terms.',
        suppressible: false,
        priority: 'urgent',
        referenceType: 'lease',
    },
    subscription_expired: {
        type: 'subscription_expired',
        category: 'billing_account',
        label: 'Subscription Expired',
        description: "The account's subscription has expired.",
        suppressible: false,
        priority: 'urgent',
        referenceType: 'subscription',
    },
    message_delivery_failed: {
        type: 'message_delivery_failed',
        category: 'communications',
        label: 'Message Delivery Failed',
        description: 'A message failed to deliver to its recipient.',
        suppressible: false,
        priority: 'urgent',
        // Real call sites disagree on referenceType (tenant-invite.service.ts uses
        // 'tenant_invite'; messaging.service.ts uses 'communication_log') — a pre-existing
        // ambiguity this change did not introduce. 'communication_log' picked as the more
        // general delivery-tracking entity; flagged as a gap (see build notes) since neither
        // is routed by resolveNotificationPath and this type falls back to /dashboard either way.
        referenceType: 'communication_log',
    },
    relay_message_delivery_failed: {
        type: 'relay_message_delivery_failed',
        category: 'communications',
        label: 'Relay Message Delivery Failed',
        description: 'A relayed message failed to deliver.',
        suppressible: false,
        priority: 'urgent',
        referenceType: 'channel_message',
    },
    new_inbound_email: {
        type: 'new_inbound_email',
        category: 'communications',
        label: 'New Inbound Email',
        description: 'A new email was received in a monitored inbox.',
        suppressible: true,
        priority: 'urgent',
        referenceType: 'email_message',
        // BR-83: hidden from the bell — a raw inbound email is not yet an actionable
        // landlord item (it may still be triaged/replied to by the agent) even though it
        // still fans out email/push per its urgent tier (unchanged).
        bellVisible: false,
    },
    agent_draft_held_for_review: {
        type: 'agent_draft_held_for_review',
        category: 'agent_review',
        label: 'Agent Draft Held for Review',
        description: 'An AI-drafted response is held pending manager review.',
        suppressible: true,
        priority: 'urgent',
        referenceType: 'agent_response_review_log',
    },
    agent_landlord_escalation: {
        type: 'agent_landlord_escalation',
        category: 'agent_review',
        label: 'Agent Escalation',
        description: 'An AI agent escalated a question for your answer.',
        suppressible: true,
        // MF-BR-79 — explicit (matches the pre-change default; called out by name in the rule).
        priority: 'urgent',
        // Default for leasing; the escalation notifier passes a per-kind reference
        // (lead | repair_request | case_escalation) — agent-platform BR-ESC-NOTIFY/BR-ESC-REMIND.
        referenceType: 'lead',
    },
    tax_form_available: {
        type: 'tax_form_available',
        category: 'tax',
        label: 'Tax Form Available',
        description: 'A new tax form is available for download.',
        suppressible: true,
        priority: 'urgent',
        referenceType: 'tax_form_1099k',
    },
    showing_requested: {
        type: 'showing_requested',
        category: 'leasing_pipeline',
        label: 'Showing Requested',
        description: 'A prospect requested a showing time.',
        suppressible: true,
        // MF-BR-79 / LS-BR-NOTIF-14 both explicitly EXCLUDE this class from the ambient demotion:
        // "showing_requested, applicant_submitted, and lead_message_received stay standard — each is an
        // inbound act by a prospect that the landlord has not already accounted for." Demoting it would
        // silently drop the email/push a landlord gets when a prospect asks for a showing.
        // `channels` is an explicit override because MF-BR-76 requires resolveChannels() to return
        // exactly what this class delivered before the tier system existed (in_app + email + push),
        // which is broader than the `standard` tier default.
        priority: 'standard',
        channels: ['in_app', 'email', 'push'],
        referenceType: 'lead',
    },
    showing_response_received: {
        type: 'showing_response_received',
        category: 'leasing_pipeline',
        label: 'Showing Response Received',
        description: 'A prospect confirmed or declined a proposed showing time.',
        suppressible: true,
        priority: 'ambient',
        referenceType: 'lead',
    },
    showing_scheduled: {
        type: 'showing_scheduled',
        category: 'leasing_pipeline',
        label: 'Showing Scheduled',
        description: 'A showing appointment was placed on the calendar.',
        suppressible: true,
        priority: 'ambient',
        referenceType: 'lead',
    },
    showing_cancelled: {
        type: 'showing_cancelled',
        category: 'leasing_pipeline',
        label: 'Showing Cancelled',
        description: 'A showing proposal or appointment was cancelled or rescheduled.',
        suppressible: true,
        priority: 'ambient',
        referenceType: 'lead',
    },
    lead_message_received: {
        type: 'lead_message_received',
        category: 'leasing_pipeline',
        label: 'New Lead Message',
        description: 'A prospect sent a new message on a Lead.',
        suppressible: true,
        // NOT one of the five re-tiered classes — a lead message is not a proposal response;
        // stays urgent (unchanged, MF-BR-76).
        priority: 'urgent',
        referenceType: 'lead',
        // BR-83: hidden from the bell even though it stays urgent (in_app/email/push fan-out
        // unchanged) — a lead message is surfaced via the inbox/channel UI, not the bell.
        bellVisible: false,
    },
    applicant_submitted: {
        type: 'applicant_submitted',
        category: 'leasing_pipeline',
        label: 'Application Submitted',
        description: 'An applicant completed and submitted their application.',
        suppressible: true,
        // MF-BR-79 / LS-BR-NOTIF-14 both explicitly EXCLUDE this class from the ambient demotion —
        // a submitted rental application is an inbound prospect act the landlord has not accounted for.
        // `channels` overrides the `standard` tier default to preserve today's delivery (MF-BR-76).
        priority: 'standard',
        channels: ['in_app', 'email', 'push'],
        referenceType: 'lead',
    },
    expense_review_queued: {
        type: 'expense_review_queued',
        category: 'expenses',
        label: 'Expense Awaiting Review',
        description: 'An expense needs review before it can be confirmed.',
        // MF-BR-78
        suppressible: true,
        priority: 'ambient',
        referenceType: 'expense',
    },
    // Rent increase (new)
    rent_increase_step_due: {
        type: 'rent_increase_step_due',
        category: 'rent_increase',
        label: 'Rent Increase Step Due',
        description: 'A rent-increase notice or document deadline is approaching.',
        suppressible: true,
        priority: 'urgent',
        referenceType: 'lease',
    },
    rent_increase_step_overdue: {
        type: 'rent_increase_step_overdue',
        category: 'rent_increase',
        label: 'Rent Increase Step Overdue',
        description: 'A rent-increase notice or document deadline has been missed.',
        suppressible: false,
        priority: 'urgent',
        referenceType: 'lease',
    },
    rent_increase_email_bounced: {
        type: 'rent_increase_email_bounced',
        category: 'rent_increase',
        label: 'Rent Increase Notice Email Bounced',
        description: 'An automatically emailed rent-increase notice bounced; the step was reopened and needs another way of service.',
        // BR-LL-36 — a bounced statutory notice means the tenant was not served; never suppressible.
        suppressible: false,
        priority: 'urgent',
        referenceType: 'lease',
    },
    rent_increase_auto_sent: {
        type: 'rent_increase_auto_sent',
        category: 'rent_increase',
        label: 'Rent Increase Notice Emailed',
        description: 'A rent-increase notice was emailed to tenants automatically.',
        suppressible: true,
        priority: 'ambient',
        referenceType: 'lease',
    },
    rent_increase_opportunity: {
        type: 'rent_increase_opportunity',
        category: 'rent_increase',
        label: 'Rent Increase Opportunity',
        description: 'This lease has become eligible for another rent increase.',
        suppressible: true,
        priority: 'urgent',
        referenceType: 'lease',
    },
    listing_period_expiring: {
        type: 'listing_period_expiring',
        category: 'billing_account',
        label: 'Listing period ending soon',
        description: "A unit's paid listing period is about to end; renew to keep the listing public.",
        // BR-93 — mandatory: a listing silently going dark at period end must not be suppressible.
        suppressible: false,
        priority: 'urgent',
        referenceType: 'listing',
    },
    repair_request_created: {
        type: 'repair_request_created',
        category: 'repairs',
        label: 'New Repair Request',
        description: 'A new repair request was submitted and needs triage/vendor assignment.',
        suppressible: true,
        // RA-REQUEST-EVENT: an unassigned repair request is an inbound act the landlord has not
        // accounted for yet — same reasoning MF-BR-79/LS-BR-NOTIF-14 apply to showing_requested /
        // applicant_submitted (both explicitly excluded from ambient demotion). `channels` is an
        // explicit override for the same reason those two use one: `standard`'s tier default is
        // in_app-only, and a new repair request should still reach a landlord's inbox.
        priority: 'standard',
        channels: ['in_app', 'email', 'push'],
        referenceType: 'repair_request',
    },
};
/**
 * Look up a notification type's registry entry.
 *
 * Throws if `type` is not registered — this is the orchestrator's gate
 * (BR-66): a new call site must register its type here before it can send.
 */
export function getNotificationTypeDefinition(type) {
    const definition = NOTIFICATION_TYPE_REGISTRY[type];
    if (!definition) {
        throw new Error(`Unregistered notification type: ${type}`);
    }
    return definition;
}
/**
 * Group every suppressible registry entry by category, in registry
 * declaration order. This is what the preferences UI iterates to render one
 * toggle row-group per category (mandatory types are never toggleable).
 */
export function listSuppressibleCategories() {
    const byCategory = new Map();
    for (const definition of Object.values(NOTIFICATION_TYPE_REGISTRY)) {
        if (!definition.suppressible)
            continue;
        const entries = byCategory.get(definition.category);
        if (entries) {
            entries.push(definition);
        }
        else {
            byCategory.set(definition.category, [definition]);
        }
    }
    return Array.from(byCategory.entries()).map(([category, entries]) => ({ category, entries }));
}
//# sourceMappingURL=registry.js.map